import {useMemo, useState} from "react";

export const useSortedData = (data, sortKey) => {
    return useMemo(() => {
        if (data) {
            if (sortKey) {
                return data.sort((a, b) => {
                    return (a[sortKey] != null) - (b[sortKey] != null) ||
                        (a[sortKey] - b[sortKey]);
                });
            }
            return data;
        }
        return null;
    }, [data, sortKey]);
}

export const useListPagination = ({ items, sort = null, pageSize = 15, listFilter = null, revealed: revealedProp = null, onRevealedChange = null }) => {
  const [expanded, setExpanded] = useState(false);

  // revealed can be controlled by the caller (revealed + onRevealedChange)
  // so other components on the page (e.g. a map) can follow the reveal toggle
  const [revealedState, setRevealedState] = useState(false);
  const revealed = revealedProp ?? revealedState;
  const setRevealed = (value) => {
    setRevealedState(value);
    onRevealedChange?.(value);
  };

  const sorted = useMemo(() => {
    if (!items || !sort) return items;
    return [...items].sort(sort);
  }, [items, sort]);

  const { primary, filtered } = useMemo(() => {
    if (!sorted) return { primary: [], filtered: [] };
    if (!listFilter) return { primary: sorted ?? [], filtered: [] };
    return {
      primary: sorted.filter(x => !listFilter(x)),
      filtered: sorted.filter(x => listFilter(x)),
    };
  }, [sorted, listFilter]);

  // undefined means "no limit" for Array.slice
  const currentLimit = expanded ? undefined : (pageSize ?? undefined);

  const { primaryItems, filteredItems } = useMemo(() => ({
    primaryItems: primary.slice(0, currentLimit),
    filteredItems: revealed ? filtered : [],
  }), [primary, currentLimit, filtered, revealed]);

  const canShowMore = (currentLimit !== undefined) && (primary.length > currentLimit);
  const canReveal = !!listFilter && !revealed && (filtered.length > 0);
  const canHide = !!listFilter && revealed && (filtered.length > 0);
  const filteredCount = filtered.length;
  const reset = () => {
    setExpanded(false);
    setRevealed(false);
  }

  return {
    primaryItems,
    filteredItems,
    filteredCount,
    canShowMore,
    canReveal,
    canHide,
    showMore: () => setExpanded(true),
    reveal: () => setRevealed(true),
    hide: () => setRevealed(false),
    reset,
  };
};
